import React, {PureComponent, useCallback} from 'react';

import {toMap} from '../../lib/helpers';
import {DATE_RANGE_OPTIONS, MILLIS_PER_HOUR} from '../../lib/timeline';
import DateTimeInput from '../../components/DateTimeInput';
import Button from '../../components/Button';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Input from '../../components/Input';

import Filter from './Filter';
import {isQuestionDeleted} from "../FormBuilderScreen/shared";

class SheetRowFilter extends PureComponent {
  state = {
    filters: this.props.filters ? this.props.filters.filters : [],
    validations: {},
    minDate: this.props.filters ? this.props.filters.minDate : null,
    maxDate: this.props.filters ? this.props.filters.maxDate : null,
    reviewState: this.props.filters ? this.props.filters.reviewState : '',
    range: getInitialRange(
      this.props.filters ? this.props.filters.minDate : null,
      this.props.filters ? this.props.filters.maxDate : null
    )
  };

  handleFilterChange = filter => {
    this.setState(prevState => ({
      filters: [
        ...prevState.filters.filter(f => f.questionId !== filter.questionId),
        filter
      ].filter(f => f.operator !== null),
      validations: Object.keys(prevState.validations).filter(key => key !== filter.questionId).reduce((map, key) => {
        map[key] = prevState.validations[key];
        return map;
      }, {})
    }));
  }

  handleApplyFilters = () => {
    const {template, onApplyFilters} = this.props;
    let {filters, minDate, maxDate, range, reviewState} = this.state;
    if (range === ALL_TIME) {
      minDate = null;
      maxDate = null;
    }
    else if (range > 0) {
      const now = new Date();
      minDate = new Date(now.getTime() - range);
      maxDate = now;
    }
    const questionsById = toMap([...template.contextQuestions, ...template.questions], question => question.id);
    const validations = filters.reduce((map, filter) => {
      const messages = Filter.validate(questionsById[filter.questionId], filter);

      if (messages.length > 0) {
        map[filter.questionId] = messages;
      }

      return map;
    }, {});

    if ((minDate && !maxDate) || (!minDate && maxDate)) {
      validations['row_date_range'] = ['Please enter a start date and end date'];
    }

    this.setState({validations});

    if (Object.keys(validations).length > 0) {
      // There was validation issues, so we won't apply any filters.
      return;
    }

    onApplyFilters({and: true, minDate, maxDate, reviewState, filters});
  }

  // The date range is kept so people can refilter within it. Choosing "All time" is how it is cleared.
  handleClearFilters = () => {
    this.setState(prevState => ({
      reviewState: '',
      filters: [],
      validations: prevState.validations['row_date_range']
        ? {row_date_range: prevState.validations['row_date_range']}
        : {}
    }));
  }

  render() {
    const {template, catalog, open, onClose, showDeleted} = this.props;
    const {filters, validations, minDate, maxDate, reviewState, range} = this.state;
    const filtersByQuestionId = toMap(filters, filter => filter.questionId);
    const questions = [...template.contextQuestions].sort((a, b) => a.order - b.order)
      .concat([...template.questions].sort((a, b) => a.order - b.order))
      .filter(question => question.type !== 'IMAGE' && question.type !== 'SIGNATURE');

    return (
      <Form onSubmit={this.handleApplyFilters}>
        <SideModal
          title="Filter Data"
          open={open}
          onClose={onClose}
          options={[
            <Button type="submit">Apply Filters</Button>,
            <Button secondary onClick={this.handleClearFilters}>Clear Filters</Button>
          ]}>
          <SideModal.Content>
            <Form.Group>
              <DateRangeFilter
                range={range}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(minDate, maxDate, range) => this.setState({minDate, maxDate, range})}
                validations={validations['row_date_range']}
              />
            </Form.Group>
            {template.reviewers.length > 0 && (
              <Form.Group>
                <Input
                  id="filter_review_state"
                  label="Review Status"
                  type="select"
                  value={reviewState}
                  onChange={e => this.setState({reviewState: e.target.value})}>
                  <option value="">All</option>
                  <option value="PENDING">Pending</option>
                  <option value="IN_REVIEW">In Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </Input>
              </Form.Group>
            )}
            {(!showDeleted ? questions.filter(question => !isQuestionDeleted(question)) : questions).map(question => (
              <Form.Group key={question.id}>
                <Filter
                  question={question}
                  catalog={catalog}
                  filter={filtersByQuestionId[question.id] || emptyFilter(question)}
                  onChange={this.handleFilterChange}
                  validations={validations[question.id] || []}
                />
              </Form.Group>
            ))}
          </SideModal.Content>
        </SideModal>
      </Form>
    );
  }
}

// No date restriction. Kept local to this filter, since DATE_RANGE_OPTIONS is shared with other screens.
const ALL_TIME = 0;
const CUSTOM_RANGE = -1;
const RANGE_OPTIONS = [{label: 'All time', range: ALL_TIME}, ...DATE_RANGE_OPTIONS];

function getInitialRange(minDate, maxDate) {
  if (!minDate || !maxDate) {
    return ALL_TIME;
  }
  const diff = maxDate.getTime() - minDate.getTime();
  const match = DATE_RANGE_OPTIONS.find(option => option.range > 0 && Math.abs(option.range - diff) < MILLIS_PER_HOUR);
  return match ? match.range : CUSTOM_RANGE;
}

// Controlled by the parent's range, so Clear Filters resets the selected option too.
function DateRangeFilter({range, minDate, maxDate, onChange, validations}) {
  const handleRangeChange = useCallback((e) => {
    const range = Number(e.target.value);
    if (range > 0) {
      const now = new Date();
      onChange(new Date(now.getTime() - range), now, range);
    } else {
      onChange(null, null, range);
    }
  }, [onChange]);

  return (
    <>
      <Input
        id="filter_date_range"
        type="select"
        label="Date Range"
        value={range}
        onChange={handleRangeChange}
      >
        {RANGE_OPTIONS.map(option => (
          <option key={option.range} value={option.range}>{option.label}</option>
        ))}
      </Input>
      {range === CUSTOM_RANGE && (
        <>
          <DateTimeInput
            id="filter_start_date"
            placeholder="Start Date"
            style={{marginTop: '0.5rem'}}
            date={minDate}
            onChange={minDate => onChange(minDate, maxDate, CUSTOM_RANGE)}
            invalid={validations}
          />
          <DateTimeInput
            placeholder="End Date"
            style={{marginTop: '0.5rem'}}
            date={maxDate}
            onChange={maxDate => onChange(minDate, maxDate, CUSTOM_RANGE)}
            invalid={validations}
            validationText={validations && validations[0]}
          />
        </>
      )}
    </>
  );
}

function emptyFilter(question) {
  return {
    questionId: question.id,
    operator: null,
    values: []
  };
}

export default SheetRowFilter;

